import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    Output,
    QueryList,
    ViewChildren,
    OnInit,
    OnDestroy
} from '@angular/core';

import { select } from '@angular-redux/store';

import { AuthService, BaseComponent, HeaderElement, Language, LogService } from '@ridango/core';
import { HeaderMenuService } from '../../services/header-menu.service';
import { HelperService } from '../../services/helper.service';
import { FeatureFlagService } from '@ridango/feature-flags';
import Env from '@ridango/core/env';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { FEATURE_FLAGS } from '@ridango/core';
import { ApplicationFacade } from '@ridango/components';
import { PermissionsFacade } from '@ridango/components/lib/permissions';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Browser } from '@capacitor/browser';

@Component({
    selector: 'gl-mobile-menu',
    templateUrl: './mobile-menu.component.html',
    styleUrls: ['./mobile-menu.component.scss']
})
export class MobileMenuComponent extends BaseComponent implements OnInit, OnDestroy {
    @select(['application', 'assetsPath']) assetsPath$;
    @select(['core', 'selectedLanguage']) selectedLanguage$;
    @select(['core', 'region', 'languages']) languages$;

    @Input() mobileMenuOpen = false;
    @Input() loggedInCustomer: string;
    @Input() isContrast: boolean;
    @Input() trapFocus = true;

    @Output() didToggleMobileMenu = new EventEmitter<any>();
    @Output() didCloseMobileMenu = new EventEmitter<any>();
    @Output() didLanguageChange = new EventEmitter<Language>();
    @Output() didLogOut = new EventEmitter<any>();
    @Output() themeToggle: EventEmitter<void> = new EventEmitter();

    @ViewChildren('menuItem') menuItemEls: QueryList<ElementRef>;
    showBookARide = true;
    menuItems: HeaderElement[] = [];
    useDropdownMenuSystem$ = this.headerMenuService.useDropdownMenuSystem$;
    dropdownMenuData$ = this.headerMenuService.getDropdownMenuData();
    private visibleMobileMenuSubject$ = new BehaviorSubject<string | null>(null);
    visibleMobileMenu$ = this.visibleMobileMenuSubject$.asObservable();

    filteredDropdownMenuItems$ = combineLatest([
        this.headerMenuService.dropdownMenuItems$,
        this.ridangoAuth.logoutBroadcast$.pipe(
            startWith(null),
            map(() => this.ridangoAuth.getLoginStatus())
        )
    ]).pipe(
        map(([items, isLoggedIn]) =>
            items.filter((item) => {
                if (!this.headerMenuService.isDropdownMenuItemAllowed(item)) {
                    return false;
                }

                if (item.id === 'account' && !isLoggedIn) {
                    return false;
                }

                return true;
            })
        )
    );

    constructor(
        private ls: LogService,
        public applicationFacade: ApplicationFacade,
        public ridangoAuth: AuthService,
        public helperService: HelperService,
        @Inject('ENV') public env: Env,
        private featureFlags: FeatureFlagService,
        public permissionsFacade: PermissionsFacade,
        private headerMenuService: HeaderMenuService
    ) {
        super();
    }

    ngOnInit() {
        if (!this.applicationFacade.isMenuOpen() && this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE) {
            this.applicationFacade.setMobileMenuOpen(true);
        }

        this.featureFlags
            .getFlags(FEATURE_FLAGS.BookARide)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((flagValue) => {
                this.showBookARide = flagValue;
            });

        this.initializeMenu();
    }

    initializeMenu(): void {
        this.menuItems = this.headerMenuService.getMenuItems();
    }

    isMenuItemAllowed(headerElement: HeaderElement): boolean {
        return this.headerMenuService.isCheckBasicConditions(headerElement);
    }

    toggleMobileMenu() {
        this.ls.log(this, 'toggleMobileMenu', '');
        this.didToggleMobileMenu.emit();
    }

    closeMobileMenu() {
        this.ls.log(this, 'closeMobileMenu', '');
        this.didCloseMobileMenu.emit();
    }

    logout() {
        this.ls.log(this, 'logOut', '');
        this.didLogOut.emit();
        this.permissionsFacade.logoutSuccess();
    }

    languageChanged(language: Language) {
        this.didLanguageChange.emit(language);
    }

    toggleContrast(): void {
        this.themeToggle.emit();
    }

    toggleMobileDropdown(menuId: string): void {
        const currentValue = this.visibleMobileMenuSubject$.value;
        const newValue = currentValue === menuId ? null : menuId;
        this.visibleMobileMenuSubject$.next(newValue);
    }

    isMobileMenuOpen(menuId: string): Observable<boolean> {
        return this.visibleMobileMenu$.pipe(map((visibleId) => visibleId === menuId));
    }

    async handleExternalLink(url: string): Promise<void> {
        await Browser.open({ url: url });
    }
}
